import React, { useState } from "react";
import Layout from "../form-layout";
import RejectionModal from "./rejection-modal";
import { checkImmediateRed } from "../../lib/eligibility";

// Generic multi-select condition checklist used by the Q7 (safety triage, one screen
// with the doc's full flat list — see heart-lung.jsx) and Q8 (comorbidity —
// comorbidities.jsx) screens.
//
// Props:
//   title           heading text
//   options         [{ key, label }]
//   formDataKey     formData/localStorage key this screen writes an array of keys to
//                    (merged into the existing array so multiple screens can share one
//                    key, e.g. all Q7 screens write into "q7Conditions")
//   nextStepId      goToStep target once submitted (and no Red flag fired)
//   requireSelection  if true, at least one option (or explicit "none") must be picked
//   nestedQuestion  optional { triggerKey, render(value, onChange) } for a follow-up
//                    question shown inline when that option is ticked (used for the
//                    eating-disorder type sub-select)
export default function ConditionChecklist({
  formData,
  setFormData,
  onBack,
  goToStep,
  title,
  helperText = "You can select multiple options. Select all that apply.",
  options,
  formDataKey,
  nextStepId,
  requireSelection = true,
  nestedQuestion,
}) {
  const existingForKey = (formData[formDataKey] || []).filter((key) =>
    options.some((o) => o.key === key)
  );
  const [selected, setSelected] = useState(existingForKey);
  const [nestedValue, setNestedValue] = useState(
    nestedQuestion ? formData[nestedQuestion.formDataKey] || "" : ""
  );
  const [rejection, setRejection] = useState(null);

  const noneKey = options.find((o) => o.key === "none")?.key;

  const toggle = (key) => {
    if (key === noneKey) {
      setSelected([key]);
      return;
    }
    setSelected((prev) => {
      const withoutNone = prev.filter((k) => k !== noneKey);
      return withoutNone.includes(key) ? withoutNone.filter((k) => k !== key) : [...withoutNone, key];
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (requireSelection && selected.length === 0) return;

    const otherScreensValues = (formData[formDataKey] || []).filter(
      (key) => !options.some((o) => o.key === key)
    );
    const merged = [...otherScreensValues, ...selected];

    const updates = { [formDataKey]: merged };
    if (nestedQuestion) updates[nestedQuestion.formDataKey] = nestedValue;

    const nextFormData = { ...formData, ...updates };
    setFormData(nextFormData);
    localStorage.setItem(formDataKey, JSON.stringify(merged));
    if (nestedQuestion) localStorage.setItem(nestedQuestion.formDataKey, nestedValue);

    const redFlag = checkImmediateRed(nextFormData);
    if (redFlag) {
      setRejection(redFlag);
      return;
    }

    goToStep(nextStepId);
  };

  return (
    <Layout goToStep={goToStep} onBack={onBack}>
      <h2 className="text-sm sm:text-[14px] font-medium text-[#000000] mb-2 mt-8">{title}</h2>
      <p className="text-gray-500 mb-6 text-sm">{helperText}</p>

      <form onSubmit={handleSubmit}>
        <ul className="flex flex-col gap-3 mb-6 list-none p-0">
          {options.map((option) => {
            const isSelected = selected.includes(option.key);
            return (
              <li key={option.key} className="flex flex-col gap-2">
                <label className="flex items-center gap-2 p-3 rounded-lg cursor-pointer border transition-all text-sm sm:text-[14px] font-medium text-[#000000]">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(option.key)}
                    className="h-4 w-4 accent-sky-400 cursor-pointer"
                  />
                  <span>{option.label}</span>
                </label>

                {nestedQuestion && nestedQuestion.triggerKey === option.key && isSelected && (
                  <div className="ml-6 mt-2">
                    {nestedQuestion.render(nestedValue, setNestedValue)}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <div className="flex justify-start">
          <button
            type="submit"
            disabled={requireSelection && selected.length === 0}
            className={`w-[210px] text-sm font-semibold rounded-lg py-3 transition-colors
              ${
                requireSelection && selected.length === 0
                  ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                  : "bg-[#35C3D9] text-white hover:bg-[#27AAC0]"
              }`}
          >
            Next →
          </button>
        </div>
      </form>

      {rejection && <RejectionModal onClose={() => setRejection(null)} />}
    </Layout>
  );
}
